/**
 * Workspaces API Client
 * Handles all communication with the workspace-service backend
 */

export interface WorkspaceSettings {
  defaultBranch: string;
  portRangeStart: number;
  portRangeEnd: number;
}

export interface Workspace {
  id: string;
  name: string;
  slug: string;
  description?: string;
  ownerId: string;
  settings: WorkspaceSettings;
  createdAt: string;
  updatedAt: string;
}

export interface CreateWorkspaceRequest {
  name: string;
  slug: string;
  description?: string;
  settings?: Partial<WorkspaceSettings>;
}

export interface UpdateWorkspaceRequest {
  name?: string;
  description?: string;
  settings?: Partial<WorkspaceSettings>;
}

// API base URL - relative path works with vite proxy (dev) and nginx (prod)
const API_BASE = '/api/workspaces';

class ApiError extends Error {
  status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

async function request<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const url = endpoint.startsWith('/') ? `${API_BASE}${endpoint}` : `${API_BASE}/${endpoint}`;

  const response = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
    body: options.body ? (typeof options.body === 'string' ? options.body : JSON.stringify(options.body)) : undefined,
    credentials: 'include', // Include cookies for auth
  });

  if (!response.ok) {
    const error = await response.text();
    throw new ApiError(response.status, error || response.statusText);
  }

  // Handle 204 No Content
  if (response.status === 204) {
    return null as T;
  }

  return response.json();
}

// Workspaces API
export const workspacesApi = {
  /**
   * List all workspaces for the current user
   */
  list: (): Promise<Workspace[]> => {
    return request<Workspace[]>('');
  },

  /**
   * Get a single workspace by ID
   */
  get: (id: string): Promise<Workspace> => {
    return request<Workspace>(`/${id}`);
  },

  /**
   * Create a new workspace
   */
  create: (data: CreateWorkspaceRequest): Promise<Workspace> => {
    return request<Workspace>('', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  /**
   * Update an existing workspace
   */
  update: (id: string, data: UpdateWorkspaceRequest): Promise<Workspace> => {
    return request<Workspace>(`/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  /**
   * Delete a workspace
   */
  delete: (id: string): Promise<void> => {
    return request<void>(`/${id}`, {
      method: 'DELETE',
    });
  },
};

export { ApiError };
